import { FC, PropsWithChildren, ReactNode } from 'react';

import { CDS_ZINDEX, Flex } from '@revenuemarket/cds';
import { colors, overlayColors, theme } from '@revenuemarket/design';
import { getPartnerByHostname } from '@revenuemarket/utils/partners/getPartnerByHostname';
import { ConfigProvider } from 'antd';
import locale from 'antd/locale/ko_KR';
import dynamic from 'next/dynamic';
import Image from 'next/image';
import { OverlayScrollbarsComponent } from 'overlayscrollbars-react';

import LightThemeLogo from '~assets/images/clobe_light_theme.svg';
import { MenuItem } from '~constants/menu-items';
import MenuList from '~features/layout/Sidebar/MenuList';
import { useCompanyId } from '~hooks/useCompanyId';
import { usePageWidth } from '~hooks/usePageWidth';
import { css } from '~libs/emotion';
import useLayoutStore from '~store/layoutStore';

import AIChatSidebar from './AiChatSidebar';
import Sidebar from './Navigation';
import { themeForClobe } from '../../../pages/_app';
import MobileBottomBar from '../Mobile/MobileBottomBar';

const InfoAlertBanner = dynamic(() => import('~components/ui/InfoAlertBanner'), {
  loading: () => <div />,
});

interface Props extends PropsWithChildren {
  authenticated?: boolean;
  leftSlot?: ReactNode;
  layoutStyle?: React.CSSProperties;
  hideNav?: boolean;
  isMobile?: boolean;
  mobileHeader?: ReactNode;
  host?: string;
}

const Wrapper = ({ children }: PropsWithChildren) => {
  return (
    <Flex
      justify="space-between"
      align="center"
      className={css`
        position: sticky;
        top: 0;
        left: 0;
        width: 100vw;
        height: 48px;
        padding: 0px 20.5px;
        z-index: ${CDS_ZINDEX.POP_UP};
        --tw-border-opacity: ${overlayColors.white[1]};
        background: ${colors.grayscaleWhite};

        border-bottom: 1px solid ${overlayColors.white[1]};

        filter: drop-shadow(0px 1px 2px rgba(0, 0, 0, 0.2));
      `}
    >
      {children}
    </Flex>
  );
};

const LayoutBase: FC<Props> = ({
  children,
  leftSlot,
  layoutStyle,
  authenticated = true,
  isMobile = false,
  mobileHeader,
  hideNav,
  host,
}) => {
  const { companyId } = useCompanyId();
  const { hideBottomBar } = useLayoutStore();
  const isPartnerIntro = !!getPartnerByHostname(host ?? '');
  const width = usePageWidth();

  const HEADER_HEIGHT = 64;
  const BOTTOM_BAR_HEIGHT = 52;

  if (isMobile) {
    return (
      <>
        {!authenticated && !isPartnerIntro ? (
          <Wrapper>
            <Image src={LightThemeLogo} alt="logo" height={16} />
          </Wrapper>
        ) : (
          <div />
        )}
        <Flex>
          <Flex
            direction="column"
            className={css`
              flex: 1;
              max-height: 100vh;
              background-color: ${authenticated
                ? colors.grayscaleWhite
                : theme.colorTokens.bgDefault};
            `}
          >
            <ConfigProvider locale={locale} theme={{ ...themeForClobe }}>
              <Flex
                className={css`
                  flex: 1;
                `}
                style={layoutStyle}
              >
                {mobileHeader}
                <Flex
                  direction="column"
                  className={css`
                    position: relative;
                    flex: 1;
                    max-width: 100vw;
                    height: calc(
                      100vh -
                        (
                          ${mobileHeader ? HEADER_HEIGHT : 0}px +
                            ${!hideBottomBar ? BOTTOM_BAR_HEIGHT : 0}px +
                            env(safe-area-inset-top, 0px) + env(safe-area-inset-bottom, 0px)
                        )
                    );
                    /* iOS Safari를 위한 동적 viewport 높이 */
                    height: calc(
                      100dvh -
                        (
                          ${mobileHeader ? HEADER_HEIGHT : 0}px +
                            ${!hideBottomBar ? BOTTOM_BAR_HEIGHT : 0}px +
                            env(safe-area-inset-top, 0px) + env(safe-area-inset-bottom, 0px)
                        )
                    );
                    margin-top: ${mobileHeader ? '64px' : '0px'};
                    overflow-y: scroll;
                  `}
                >
                  {children}
                </Flex>
              </Flex>
            </ConfigProvider>
          </Flex>
        </Flex>
        {authenticated && !hideBottomBar && <MobileBottomBar />}
      </>
    );
  }

  if (isMobile && !companyId) {
    return (
      <Flex direction="column" gap={16}>
        <Wrapper>
          <Image src={LightThemeLogo} alt="logo" height={16} />
        </Wrapper>
        <ConfigProvider locale={locale} theme={{ ...themeForClobe }}>
          {children}
        </ConfigProvider>
      </Flex>
    );
  }

  // PC용 레이아웃
  return (
    <>
      {!authenticated ? (
        <Wrapper>
          <Image src={LightThemeLogo} alt="logo" height={16} />
        </Wrapper>
      ) : (
        <div />
      )}
      <Flex>
        {/* Left */}
        {authenticated && !isMobile && !hideNav && (
          <ConfigProvider locale={locale} theme={{ ...themeForClobe }}>
            <Sidebar isPartnerIntro={isPartnerIntro} />
          </ConfigProvider>
        )}

        {/* Main */}
        <Flex
          direction="column"
          className={css`
            flex: 1;
            height: 100vh;
            background-color: ${authenticated
              ? colors.grayscaleWhite
              : theme.colorTokens.bgDefault};
          `}
        >
          <ConfigProvider locale={locale} theme={{ ...themeForClobe }}>
            <Flex
              className={css`
                flex: 1;
              `}
              style={layoutStyle}
            >
              {leftSlot}
              <OverlayScrollbarsComponent
                className={css`
                  width: ${width};
                  height: 100vh;
                  overflow: auto;
                  border: none;
                `}
                defer
              >
                <Flex
                  direction="column"
                  className={css`
                    position: relative;
                    flex: 1;
                    max-width: 100vw;
                  `}
                >
                  {!isMobile && authenticated ? <InfoAlertBanner /> : null}
                  {children}
                </Flex>
              </OverlayScrollbarsComponent>
            </Flex>
          </ConfigProvider>
        </Flex>

        {/* Right: AI 채팅 사이드바 */}
        <AIChatSidebar />
      </Flex>
    </>
  );
};

const LayoutSidebar = ({ menuItems }: { menuItems: MenuItem[] }) => {
  return (
    <aside
      style={{
        borderRight: `1px solid ${colors.grayscale200}`,
      }}
    >
      <Flex
        direction="column"
        className={css`
          position: sticky;
          left: 0;
          top: 0;
          width: 200px;
          padding-top: 20px;
        `}
      >
        <MenuList menuItems={menuItems} />
      </Flex>
    </aside>
  );
};

const Layout = Object.assign(LayoutBase, {
  Sidebar: LayoutSidebar,
});

export default Layout;
